<template>
  <div class="container">
    <div class="content--wrapper">
      <h3>Crea el contenido de tu plantilla</h3>

      <p>
        Puedes añadir parámetros escribiendo
        {{ paramString() }} y colocando un número que indique el orden. Por ejemplo: Hola
        {{ paramString(1) }}, gracias por contactarnos por {{ paramString(2) }}. Recuerda que se
        consumirá 50 caracteres por cada parámetro añadido.
      </p>

      <a-form layout="vertical" class="form" ref="formRef" :model="formState">
        <a-form-item label="Contenido de la plantilla" name="content" :rules="contentRules">
          <!-- showCount -->
          <a-textarea
            v-model:value="formState.content"
            :autoSize="{ minRows: 7, maxRows: 12 }"
            :showCount="{ formatter: countFormatter }"
            :maxlength="contentMaxLength"
            @mousedown="handleDeselect"
            @focus="handleDeselect"
            @blur="handleDeselect"
            @select="handleSelect"
            @keydown="handleKeydown"
            placeholder="Hola, tu código de {{1}} es {{2}}"
          >
          </a-textarea>

          <p class="warning" v-if="contentMaxLength == formState.content.length">
            Llegaste al límite de caracteres.
          </p>
        </a-form-item>
      </a-form>
    </div>

    <SmsTemplatePhone
      :content="formState.content"
      preview="Aquí se visualizará tu plantilla de SMS"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, toRaw, watch } from "vue";
import { FormInstance } from "ant-design-vue";
import { Rule } from "ant-design-vue/es/form";

import { ContentFormState } from "@/@types/channels/sms/dto";
import { templateParamRegex } from "@/app/channels/utils/regex";
import { isContentConsecutive } from "@/app/shared/utils/helpers";
import { SmsTemplate } from "@/@types/channels/sms";

import SmsTemplatePhone from "@/app/channels/components/modals/sms/templates/SmsTemplatePhone.vue";
import { cloneDeep, isEqual } from "lodash";

const paramString = (num?: number) => `{{${num || ""}}}`;

const props = defineProps<{ smsTemplate?: SmsTemplate }>();

const emit = defineEmits<{ toggleButton: [disabled: boolean] }>();

const formRef = ref<FormInstance>();
const formState: ContentFormState = reactive({
  content: props.smsTemplate?.template || "",
});

const numberOfParametersInContent = computed(() => {
  const parametersInContent = formState.content.match(templateParamRegex);

  return parametersInContent?.length ?? 0;
});

const contentMaxLength = computed(() => {
  return 560 - numberOfParametersInContent.value * 50 + totalCharactersParameters();
});

const totalCharactersParameters = () => {
  let match;
  let totalCharacters = 0;

  while ((match = templateParamRegex.exec(formState.content)) !== null) {
    totalCharacters += match[0].length;
  }

  return totalCharacters;
};

const countFormatter = ({ count, maxlength }) => {
  console.log(count, "count");
  return `${count - totalCharactersParameters()} / ${maxlength - totalCharactersParameters()}`;
};

const validateParametersPattern = async (_rule: Rule, value: string) => {
  const parametersInContent = value.match(templateParamRegex);

  if (!parametersInContent) return Promise.resolve();

  const numbersInParameters = parametersInContent
    .flatMap((param) => param.match(/[0-9]+/g))
    .map((num) => Number(num));

  const isValidContent =
    numbersInParameters?.[0] === 1 && isContentConsecutive(numbersInParameters);

  if (isValidContent) return Promise.resolve();

  return Promise.reject(
    "Los parámetros deben comenzar con el número 1, ser consecutivos y no repetirse"
  );
};

const validateContentLimit = async (_rule: Rule, value: string) => {
  const isValidContent = contentMaxLength.value >= value.length - totalCharactersParameters();
  if (isValidContent) return Promise.resolve();

  return Promise.reject("Superaste el límite de caracteres");
};

const contentRules: Rule[] = [
  { required: true, message: "Campo requerido" },
  { validator: validateParametersPattern },
  { validator: validateContentLimit },
];

const referenceFormState = cloneDeep(toRaw(formState));

watch(formState, () => {
  formRef.value
    ?.validate()
    .then(() => emit("toggleButton", isEqual(referenceFormState, formState)))
    .catch(() => emit("toggleButton", true));
});

const selectionRange = ref({ start: 0, end: 0 });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleSelect = (event: any) => {
  const textarea = event.target;

  selectionRange.value = {
    start: textarea.selectionStart,
    end: textarea.selectionEnd,
  };
};

const handleDeselect = () => {
  selectionRange.value = {
    start: 0,
    end: 0,
  };
};

const keydownDelete = ref(false);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleKeydown = (event: any) => {
  if (event.key === "Backspace" || event.key === "Delete" || (event.ctrlKey && event.key === "x")) {
    keydownDelete.value = true;
  }
};

watch(
  () => formState.content,
  async (newContent, oldContent) => {
    if (newContent && oldContent) {
      const caractersDeleted = oldContent.length - newContent.length;
      if (caractersDeleted > 1) {
        const caractersSelected = selectionRange.value.end - selectionRange.value.start;
        if (keydownDelete.value) {
          if (caractersSelected > 0 && caractersDeleted > caractersSelected) {
            formState.content = oldContent.slice(0, oldContent.length - caractersSelected);
          } else if (caractersSelected == 0) {
            formState.content = oldContent.slice(0, oldContent.length - 1);
          }
        } else {
          formState.content = oldContent;
        }
      }
    }
    keydownDelete.value = false;
    handleDeselect();
  },
  { immediate: true, deep: true }
);

defineExpose({ formState });
</script>

<style scoped lang="sass">
.container
  display: flex
  gap: 30px
  align-items: flex-start

.content--wrapper
  min-width: 0

h3
  font-size: 24px
  font-weight: 600
  color: $gray-9
  margin-bottom: 4px
  line-height: 32px

p
  color: $gray-8

.warning
  margin: 0
  color: #D48806
</style>
